import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DeadLetterMessageDetailModal } from '../../components/service-bus/DeadLetterMessageDetailModal';
import { DeadLetterTable } from '../../components/service-bus/DeadLetterTable';
import { QueueHealthCards } from '../../components/service-bus/QueueHealthCards';
import { useToast } from '../../contexts/ToastContext';
import { apiClient } from '../../services/api';
import type {
  ServiceBusDeadLetterMessageDetail,
  ServiceBusDeadLetterPagedResult,
  ServiceBusHealthSummary,
  ServiceBusQueueMetrics,
} from '../../services/api';
import { formatDateTime } from '../../utils/dateFormat';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const AUTO_REFRESH_OPTIONS = [
  { label: '5s', value: 5000 },
  { label: '10s', value: 10000 },
  { label: '30s', value: 30000 },
  { label: '60s', value: 60000 },
] as const;

type SortColumn = 'queueName' | 'accessedAt' | 'activeMessageCount' | 'deadLetterMessageCount' | 'scheduledMessageCount' | 'sizeInBytes';
type SortDirection = 'asc' | 'desc';

export function ServiceBusMonitoringPage() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [expandedQueueName, setExpandedQueueName] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [autoRefreshIntervalMs, setAutoRefreshIntervalMs] = useState(10000);
  const [selectedMessage, setSelectedMessage] = useState<{ queueName: string; sequenceNumber: number } | null>(null);
  const [confirmDialogState, setConfirmDialogState] = useState<ServiceBusPageConfirmDialogState>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editedBody, setEditedBody] = useState('');
  const [settingsQueue, setSettingsQueue] = useState<ServiceBusQueueMetrics | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>('queueName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const refreshInterval = autoRefreshEnabled ? autoRefreshIntervalMs : false;

  const healthQuery = useQuery<ServiceBusHealthSummary, Error>({
    queryKey: ['admin-service-bus-health'],
    queryFn: () => apiClient.getServiceBusHealth(),
    refetchInterval: refreshInterval,
  });

  const queuesQuery = useQuery<ServiceBusQueueMetrics[], Error>({
    queryKey: ['admin-service-bus-queues'],
    queryFn: () => apiClient.getServiceBusQueues(),
    refetchInterval: refreshInterval,
  });

  const deadLettersQuery = useQuery<ServiceBusDeadLetterPagedResult, Error>({
    queryKey: ['admin-service-bus-dead-letters', expandedQueueName, page, pageSize],
    queryFn: () => apiClient.getServiceBusDeadLetterMessages(expandedQueueName!, page, pageSize),
    enabled: Boolean(expandedQueueName),
    refetchInterval: refreshInterval,
  });

  const detailQuery = useQuery<ServiceBusDeadLetterMessageDetail, Error>({
    queryKey: ['admin-service-bus-dead-letter-detail', selectedMessage?.queueName, selectedMessage?.sequenceNumber],
    queryFn: () => apiClient.getServiceBusDeadLetterMessageDetail(selectedMessage!.queueName, selectedMessage!.sequenceNumber),
    enabled: Boolean(selectedMessage),
    refetchInterval: refreshInterval,
  });

  const summary = useMemo(() => {
    const queues = queuesQuery.data ?? [];
    const fromQueues = {
      totalQueues: queues.length,
      totalActiveMessages: queues.reduce((total, queue) => total + queue.activeMessageCount, 0),
      totalDeadLetterMessages: queues.reduce((total, queue) => total + queue.deadLetterMessageCount, 0),
      totalScheduledMessages: queues.reduce((total, queue) => total + queue.scheduledMessageCount, 0),
    };

    if (healthQuery.data) {
      return {
        totalQueues: healthQuery.data.totalQueues ?? fromQueues.totalQueues,
        totalActiveMessages: healthQuery.data.totalActiveMessages ?? fromQueues.totalActiveMessages,
        totalDeadLetterMessages: healthQuery.data.totalDeadLetterMessages ?? fromQueues.totalDeadLetterMessages,
        totalScheduledMessages: fromQueues.totalScheduledMessages,
      };
    }

    return fromQueues;
  }, [healthQuery.data, queuesQuery.data]);

  const sortedQueues = useMemo(() => {
    const queues = [...(queuesQuery.data ?? [])];
    queues.sort((left, right) => {
      const leftValue = getSortValue(left, sortColumn);
      const rightValue = getSortValue(right, sortColumn);
      let result = 0;

      if (typeof leftValue === 'string' && typeof rightValue === 'string') {
        result = leftValue.localeCompare(rightValue);
      } else {
        result = Number(leftValue) - Number(rightValue);
      }

      return sortDirection === 'asc' ? result : result * -1;
    });

    return queues;
  }, [queuesQuery.data, sortColumn, sortDirection]);

  const resubmitAllMutation = useMutation({
    mutationFn: (queueName: string) => apiClient.resubmitAllServiceBusDeadLetterMessages(queueName),
    onSuccess: (_, queueName) => {
      invalidateServiceBusQueries(queryClient);
      addToast(`Resubmitting all dead letter messages for ${queueName}.`, 'success');
      setConfirmDialogState(null);
    },
    onError: (error: Error) => {
      addToast(error.message || 'Failed to resubmit all dead letter messages.', 'error');
    },
  });

  const purgeQueueMutation = useMutation({
    mutationFn: (queueName: string) => apiClient.purgeServiceBusDeadLetterQueue(queueName),
    onSuccess: (_, queueName) => {
      invalidateServiceBusQueries(queryClient);
      addToast(`Purge started for ${queueName}.`, 'success');
      setConfirmDialogState(null);
      setSelectedMessage(null);
    },
    onError: (error: Error) => {
      addToast(error.message || 'Failed to purge dead letter queue.', 'error');
    },
  });

  const resubmitMessageMutation = useMutation({
    mutationFn: (payload: { queueName: string; sequenceNumber: number }) =>
      apiClient.resubmitServiceBusDeadLetterMessages(payload.queueName, [payload.sequenceNumber]),
    onSuccess: (_, payload) => {
      invalidateServiceBusQueries(queryClient);
      addToast(`Message ${payload.sequenceNumber} resubmitted.`, 'success');
      setConfirmDialogState(null);
      setSelectedMessage(null);
    },
    onError: (error: Error) => {
      addToast(error.message || 'Failed to resubmit dead letter message.', 'error');
    },
  });

  const deleteMessageMutation = useMutation({
    mutationFn: (payload: { queueName: string; sequenceNumber: number }) =>
      apiClient.deleteServiceBusDeadLetterMessages(payload.queueName, [payload.sequenceNumber]),
    onSuccess: (_, payload) => {
      invalidateServiceBusQueries(queryClient);
      addToast(`Message ${payload.sequenceNumber} deleted from dead letter queue.`, 'success');
      setConfirmDialogState(null);
      setSelectedMessage(null);
    },
    onError: (error: Error) => {
      addToast(error.message || 'Failed to delete dead letter message.', 'error');
    },
  });

  const editAndResubmitMutation = useMutation({
    mutationFn: (payload: { queueName: string; sequenceNumber: number; modifiedBody: string }) =>
      apiClient.resubmitServiceBusDeadLetterMessageWithEdit(payload.queueName, payload.sequenceNumber, payload.modifiedBody),
    onSuccess: () => {
      invalidateServiceBusQueries(queryClient);
      addToast('Message edited and resubmitted.', 'success');
      setIsEditModalOpen(false);
      setSelectedMessage(null);
    },
    onError: (error: Error) => {
      addToast(error.message || 'Failed to edit and resubmit message.', 'error');
    },
  });

  const refreshAll = async () => {
    await Promise.all([
      healthQuery.refetch(),
      queuesQuery.refetch(),
      expandedQueueName ? deadLettersQuery.refetch() : Promise.resolve(),
      selectedMessage ? detailQuery.refetch() : Promise.resolve(),
    ]);
  };

  const isAnyActionPending =
    resubmitAllMutation.isPending ||
    purgeQueueMutation.isPending ||
    resubmitMessageMutation.isPending ||
    deleteMessageMutation.isPending ||
    editAndResubmitMutation.isPending;

  const editJsonValidation = useMemo(() => validateJson(editedBody), [editedBody]);

  const toggleQueueRow = (queueName: string) => {
    setExpandedQueueName((current) => {
      const next = current === queueName ? null : queueName;
      setPage(1);
      setSelectedMessage(null);
      return next;
    });
  };

  const toggleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortColumn(column);
    setSortDirection('asc');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Service Bus Monitoring</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Monitor queue health, inspect dead letters, and recover messages.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={autoRefreshEnabled}
              onChange={(event) => setAutoRefreshEnabled(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-700 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900"
              aria-label="Enable auto refresh"
            />
            Auto refresh
          </label>
          <label htmlFor="service-bus-refresh-interval" className="sr-only">
            Auto refresh interval
          </label>
          <select
            id="service-bus-refresh-interval"
            value={String(autoRefreshIntervalMs)}
            onChange={(event) => setAutoRefreshIntervalMs(Number(event.target.value))}
            disabled={!autoRefreshEnabled}
            className="rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 transition-colors hover:border-primary-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          >
            {AUTO_REFRESH_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void refreshAll()}
            disabled={healthQuery.isFetching || queuesQuery.isFetching}
            className="inline-flex items-center gap-2 rounded-md bg-primary-700 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshIcon className={`h-4 w-4 ${(healthQuery.isFetching || queuesQuery.isFetching) ? 'animate-spin' : ''}`} />
            {healthQuery.isFetching || queuesQuery.isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <QueueHealthCards summary={summary} healthSummary={healthQuery.data} isLoading={healthQuery.isLoading && !healthQuery.data} />

      {queuesQuery.isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <div className="animate-pulse space-y-2">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="h-9 rounded bg-gray-200 dark:bg-gray-700" />
            ))}
          </div>
        </div>
      ) : null}

      {!queuesQuery.isLoading && queuesQuery.isError ? (
        <div className="card border border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300" role="alert">
          Failed to load queues. {queuesQuery.error?.message}
        </div>
      ) : null}

      {!queuesQuery.isLoading && !queuesQuery.isError && (queuesQuery.data?.length ?? 0) === 0 ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">No queues available.</div>
      ) : null}

      {!queuesQuery.isLoading && !queuesQuery.isError && (queuesQuery.data?.length ?? 0) > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <caption className="sr-only">Service Bus queues</caption>
            <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800">
              <tr>
                <SortableHeader
                  label="Queue Name"
                  isActive={sortColumn === 'queueName'}
                  direction={sortDirection}
                  onClick={() => toggleSort('queueName')}
                />
                <SortableHeader
                  label="Last Accessed"
                  isActive={sortColumn === 'accessedAt'}
                  direction={sortDirection}
                  onClick={() => toggleSort('accessedAt')}
                />
                <SortableHeader
                  label="Active"
                  className="text-right"
                  isActive={sortColumn === 'activeMessageCount'}
                  direction={sortDirection}
                  onClick={() => toggleSort('activeMessageCount')}
                />
                <SortableHeader
                  label="Dead Letter"
                  className="text-right"
                  isActive={sortColumn === 'deadLetterMessageCount'}
                  direction={sortDirection}
                  onClick={() => toggleSort('deadLetterMessageCount')}
                />
                <SortableHeader
                  label="Scheduled"
                  className="text-right"
                  isActive={sortColumn === 'scheduledMessageCount'}
                  direction={sortDirection}
                  onClick={() => toggleSort('scheduledMessageCount')}
                />
                <SortableHeader
                  label="Size"
                  className="text-right"
                  isActive={sortColumn === 'sizeInBytes'}
                  direction={sortDirection}
                  onClick={() => toggleSort('sizeInBytes')}
                />
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-200">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-900">
              {sortedQueues.map((queue) => {
                const isExpanded = expandedQueueName === queue.queueName;

                return (
                  <Fragment key={queue.queueName}>
                    <tr
                      className={`cursor-pointer transition-colors hover:bg-primary-50/40 dark:hover:bg-primary-900/20 ${isExpanded ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''}`}
                      onClick={() => toggleQueueRow(queue.queueName)}
                    >
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-gray-100">{queue.queueName}</span>
                          <StatusBadge status={queue.status} />
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">
                        {queue.accessedAt ? formatDateTime(queue.accessedAt) : 'Never'}
                      </td>
                      <td className="px-3 py-2.5 text-right font-medium text-gray-900 dark:text-gray-100">{queue.activeMessageCount}</td>
                      <td className={`px-3 py-2.5 text-right font-medium ${queue.deadLetterMessageCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
                        {queue.deadLetterMessageCount}
                      </td>
                      <td className="px-3 py-2.5 text-right font-medium text-gray-900 dark:text-gray-100">{queue.scheduledMessageCount}</td>
                      <td className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-200">{formatBytes(queue.sizeInBytes)}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setSettingsQueue(queue);
                            }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                            aria-label={`Open settings for ${queue.queueName}`}
                          >
                            <SettingsIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleQueueRow(queue.queueName);
                            }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                            aria-expanded={isExpanded}
                            aria-controls={`service-bus-queue-${queue.queueName}`}
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${queue.queueName}`}
                          >
                            <ChevronIcon className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {isExpanded ? (
                      <tr id={`service-bus-queue-${queue.queueName}`}>
                        <td colSpan={7} className="bg-gray-50 px-3 py-3 dark:bg-gray-950/70">
                          <div className="space-y-3 rounded-md border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Dead Letter Queue</h3>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setConfirmDialogState({ action: 'resubmit-all', queueName: queue.queueName })}
                                  disabled={isAnyActionPending}
                                  className="rounded-md bg-primary-700 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  Resubmit All
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmDialogState({ action: 'purge-queue', queueName: queue.queueName })}
                                  disabled={isAnyActionPending}
                                  className="rounded-md border border-red-600 px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:text-red-300 dark:hover:bg-red-900/20"
                                >
                                  Purge Queue
                                </button>
                              </div>
                            </div>
                            <DeadLetterTable
                              queueName={queue.queueName}
                              data={deadLettersQuery.data}
                              isLoading={deadLettersQuery.isLoading}
                              isError={deadLettersQuery.isError}
                              errorMessage={deadLettersQuery.error?.message}
                              pageSize={pageSize}
                              pageSizeOptions={PAGE_SIZE_OPTIONS}
                              isBusy={isAnyActionPending}
                              onPageSizeChange={(value) => {
                                setPageSize(value);
                                setPage(1);
                              }}
                              onPreviousPage={() => setPage((current) => Math.max(1, current - 1))}
                              onNextPage={() => {
                                if (!deadLettersQuery.data) {
                                  return;
                                }
                                setPage((current) => Math.min(deadLettersQuery.data.totalPages, current + 1));
                              }}
                              onSelectMessage={(sequenceNumber) => setSelectedMessage({ queueName: queue.queueName, sequenceNumber })}
                            />
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {settingsQueue ? (
        <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/70 p-4">
          <div role="dialog" aria-modal="true" aria-labelledby="service-bus-queue-settings-title" className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl dark:bg-gray-800">
            <h3 id="service-bus-queue-settings-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Queue Settings
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {settingsQueue.queueName}
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setConfirmDialogState({ action: 'resubmit-all', queueName: settingsQueue.queueName });
                  setSettingsQueue(null);
                }}
                disabled={isAnyActionPending}
                className="rounded-md bg-primary-700 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Resubmit All Dead Letter Messages
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmDialogState({ action: 'purge-queue', queueName: settingsQueue.queueName });
                  setSettingsQueue(null);
                }}
                disabled={isAnyActionPending}
                className="rounded-md border border-red-600 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:text-red-300 dark:hover:bg-red-900/20"
              >
                Purge Dead Letter Queue
              </button>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setSettingsQueue(null)}
                disabled={isAnyActionPending}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <DeadLetterMessageDetailModal
        isOpen={Boolean(selectedMessage)}
        queueName={selectedMessage?.queueName}
        sequenceNumber={selectedMessage?.sequenceNumber}
        detail={detailQuery.data}
        isLoading={detailQuery.isLoading}
        isError={detailQuery.isError}
        errorMessage={detailQuery.error?.message}
        isActionPending={isAnyActionPending}
        onClose={() => setSelectedMessage(null)}
        onResubmit={() => {
          if (!selectedMessage) {
            return;
          }
          setConfirmDialogState({
            action: 'resubmit-message',
            queueName: selectedMessage.queueName,
            sequenceNumber: selectedMessage.sequenceNumber,
          });
        }}
        onDelete={() => {
          if (!selectedMessage) {
            return;
          }
          setConfirmDialogState({
            action: 'delete-message',
            queueName: selectedMessage.queueName,
            sequenceNumber: selectedMessage.sequenceNumber,
          });
        }}
        onEditAndResubmit={(body) => {
          setEditedBody(formatJsonBody(body));
          setIsEditModalOpen(true);
        }}
      />

      {isEditModalOpen && selectedMessage ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="service-bus-edit-resubmit-title"
            className="w-full max-w-4xl rounded-lg bg-white shadow-xl dark:bg-gray-800"
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              <h3 id="service-bus-edit-resubmit-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Edit and Resubmit Message
              </h3>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                disabled={editAndResubmitMutation.isPending}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </div>
            <div className="space-y-3 p-6">
              <label htmlFor="service-bus-edit-body" className="block text-sm font-medium text-gray-800 dark:text-gray-200">
                Message body (JSON)
              </label>
              <textarea
                id="service-bus-edit-body"
                value={editedBody}
                onChange={(event) => setEditedBody(event.target.value)}
                rows={18}
                spellCheck={false}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
              <p className={`text-sm ${editJsonValidation.isValid ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                {editJsonValidation.isValid ? 'JSON is valid.' : `JSON is invalid: ${editJsonValidation.message}`}
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                disabled={editAndResubmitMutation.isPending}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  editAndResubmitMutation.mutate({
                    queueName: selectedMessage.queueName,
                    sequenceNumber: selectedMessage.sequenceNumber,
                    modifiedBody: editedBody,
                  })
                }
                disabled={!editJsonValidation.isValid || editAndResubmitMutation.isPending}
                className="rounded-md bg-primary-700 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {editAndResubmitMutation.isPending ? 'Resubmitting...' : 'Save and Resubmit'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmDialogState ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4">
          <div role="dialog" aria-modal="true" aria-labelledby="service-bus-confirm-title" className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl dark:bg-gray-800">
            <h3 id="service-bus-confirm-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {getConfirmTitle(confirmDialogState)}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{getConfirmMessage(confirmDialogState)}</p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDialogState(null)}
                disabled={isAnyActionPending}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  handleConfirmAction(confirmDialogState, {
                    purgeQueueMutation,
                    resubmitAllMutation,
                    resubmitMessageMutation,
                    deleteMessageMutation,
                  })
                }
                disabled={isAnyActionPending}
                className="rounded-md bg-primary-700 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isAnyActionPending ? 'Working...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SortableHeader({
  label,
  className,
  isActive,
  direction,
  onClick,
}: {
  label: string;
  className?: string;
  isActive: boolean;
  direction: SortDirection;
  onClick: () => void;
}) {
  return (
    <th aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : undefined} className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-200 ${className ?? ''}`}>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 transition-colors hover:text-gray-900 dark:hover:text-gray-100 ${className?.includes('text-right') ? 'ml-auto' : ''}`}
      >
        {label}
        <span aria-hidden="true" className={`text-[10px] ${isActive ? 'opacity-100' : 'opacity-40'}`}>
          {isActive ? (direction === 'asc' ? '▲' : '▼') : '▼'}
        </span>
      </button>
    </th>
  );
}

function StatusBadge({ status }: { status?: string }) {
  const label = getStatusLabel(status);
  const className = getStatusClassName(status);
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>● {label}</span>;
}

function getStatusLabel(status?: string) {
  switch (status?.toLowerCase()) {
    case 'healthy':
      return 'Healthy';
    case 'warning':
      return 'Warning';
    case 'critical':
      return 'Critical';
    default:
      return 'Unknown';
  }
}

function getStatusClassName(status?: string) {
  switch (status?.toLowerCase()) {
    case 'healthy':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'warning':
      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
    case 'critical':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }
}

function getSortValue(queue: ServiceBusQueueMetrics, column: SortColumn): string | number {
  switch (column) {
    case 'queueName':
      return queue.queueName;
    case 'accessedAt':
      return queue.accessedAt ? Date.parse(queue.accessedAt) : 0;
    case 'activeMessageCount':
      return queue.activeMessageCount;
    case 'deadLetterMessageCount':
      return queue.deadLetterMessageCount;
    case 'scheduledMessageCount':
      return queue.scheduledMessageCount;
    case 'sizeInBytes':
      return queue.sizeInBytes;
  }
}

function validateJson(value: string): { isValid: boolean; message: string } {
  if (!value.trim()) {
    return { isValid: false, message: 'Body is empty' };
  }
  try {
    JSON.parse(value);
    return { isValid: true, message: 'Valid JSON' };
  } catch (error) {
    return {
      isValid: false,
      message: error instanceof Error ? error.message : 'Invalid JSON',
    };
  }
}

function formatJsonBody(value?: string | null): string {
  if (!value) {
    return 'No message body available.';
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

type ServiceBusPageConfirmDialogState =
  | { action: 'purge-queue' | 'resubmit-all'; queueName: string }
  | { action: 'resubmit-message' | 'delete-message'; queueName: string; sequenceNumber: number }
  | null;

function getConfirmTitle(state: NonNullable<ServiceBusPageConfirmDialogState>) {
  switch (state.action) {
    case 'purge-queue':
      return `Purge queue ${state.queueName}?`;
    case 'resubmit-all':
      return `Resubmit all dead letter messages from ${state.queueName}?`;
    case 'resubmit-message':
      return `Resubmit message ${state.sequenceNumber}?`;
    case 'delete-message':
      return `Delete message ${state.sequenceNumber}?`;
  }
}

function getConfirmMessage(state: NonNullable<ServiceBusPageConfirmDialogState>) {
  switch (state.action) {
    case 'purge-queue':
      return 'This permanently removes all dead letter messages from the queue.';
    case 'resubmit-all':
      return 'All dead letter messages in this queue will be resubmitted for processing.';
    case 'resubmit-message':
      return 'This message will be resubmitted for processing.';
    case 'delete-message':
      return 'This message will be permanently deleted from dead letter queue.';
  }
}

function handleConfirmAction(
  state: NonNullable<ServiceBusPageConfirmDialogState>,
  actions: {
    purgeQueueMutation: {
      mutate: (queueName: string) => void;
    };
    resubmitAllMutation: {
      mutate: (queueName: string) => void;
    };
    resubmitMessageMutation: {
      mutate: (payload: { queueName: string; sequenceNumber: number }) => void;
    };
    deleteMessageMutation: {
      mutate: (payload: { queueName: string; sequenceNumber: number }) => void;
    };
  },
) {
  switch (state.action) {
    case 'purge-queue':
      actions.purgeQueueMutation.mutate(state.queueName);
      break;
    case 'resubmit-all':
      actions.resubmitAllMutation.mutate(state.queueName);
      break;
    case 'resubmit-message':
      actions.resubmitMessageMutation.mutate({ queueName: state.queueName, sequenceNumber: state.sequenceNumber });
      break;
    case 'delete-message':
      actions.deleteMessageMutation.mutate({ queueName: state.queueName, sequenceNumber: state.sequenceNumber });
      break;
  }
}

function invalidateServiceBusQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['admin-service-bus-health'] });
  queryClient.invalidateQueries({ queryKey: ['admin-service-bus-queues'] });
  queryClient.invalidateQueries({ queryKey: ['admin-service-bus-dead-letters'] });
  queryClient.invalidateQueries({ queryKey: ['admin-service-bus-dead-letter-detail'] });
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12a9 9 0 0 1 15-6l3 3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 0 1-15 6l-3-3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 3v6h-6M3 21v-6h6" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <circle cx="12" cy="12" r="3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .9 1.7 1.7 0 0 1-3 0 1.7 1.7 0 0 0-1-.9 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.9-1 1.7 1.7 0 0 1 0-3 1.7 1.7 0 0 0 .9-1 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.9 1.7 1.7 0 0 1 3 0 1.7 1.7 0 0 0 1 .9 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.42.17.76.5.93.92a1.7 1.7 0 0 1 0 3c-.17.42-.5.76-.93.93A1.7 1.7 0 0 0 19.4 15z" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  );
}
